import { WorkflowAction } from './WorkflowAction';

// One discovered item's fate at disposition: whether it became a retrieval run.
// `invalid` marks an item the sweep captured without a usable pointer.
export interface DispositionItem {
  referenceOrUrl?: string | null;
  patientName?: string | null;
  disposition: 'created' | 'skipped' | 'invalid';
  workflowId?: string | null;
}

export interface DiscoveryDisposition {
  created: number;
  skipped: number;
  superseded: number;
  spawnedWorkflowIds: string[];
  items: DispositionItem[];
}

/**
 * The summary as a model, from wherever it was read. The same hash reaches the front end two
 * ways — as this action's own output, and as the workflow-level value the engine publishes when
 * the action completes (which is all a list row carries) — and both must normalize identically
 * or a scan card and its results table could disagree about what the sweep did.
 *
 * Null on a pre-verdict summary shape (runs disposed before per-item verdicts existed), so
 * callers keep their fallback rather than reading zeros as facts.
 */
export function toDiscoveryDisposition(value: unknown): DiscoveryDisposition | null {
  const summary = value as Partial<DiscoveryDisposition> | undefined;
  if (!summary || typeof summary !== 'object' || !Array.isArray(summary.items)) return null;
  return {
    created: summary.created ?? 0,
    skipped: summary.skipped ?? 0,
    superseded: summary.superseded ?? 0,
    spawnedWorkflowIds: Array.isArray(summary.spawnedWorkflowIds) ? summary.spawnedWorkflowIds : [],
    items: summary.items,
  };
}

/**
 * The entries that BECAME WORK — the one definition of a sweep's result, used by the card's
 * count and by the results table's rows alike. Deliberately one function rather than two
 * filters: the count and the list it links to can then never disagree, and `created` (the
 * integer) is not consulted at all, so there is a single source of the number.
 *
 * `skipped` and `invalid` entries are absent from this by design. Every item here has a
 * `workflow_id` — the api writes one exactly when the disposition is `created`.
 */
export function createdItems(disposition: DiscoveryDisposition | null): DispositionItem[] {
  if (!disposition) return [];
  return disposition.items.filter((item) => item.disposition === 'created');
}

// The DisposePortalActivity action kind (OLIO-2756): turns each discovered item into its
// own portal_automation retrieval run and completes with a `discovery_disposition`
// summary. The sweep card reads it to show only the items that became work.
export class DisposePortalActivityAction extends WorkflowAction {
  // Null until disposition has run (or on a pre-verdict summary shape), so the card can
  // fall back to rendering the raw discovered list.
  get disposition(): DiscoveryDisposition | null {
    return toDiscoveryDisposition(this.outputFor('discovery_disposition'));
  }
}
